import React from 'react';
import { useTable, useSortBy, usePagination, useGlobalFilter, ColumnInstance } from 'react-table';
import { useMounted } from '@homesusa/core';
import { FetchData, TableOptions } from '../../interfaces';
import { getSortColumn } from '../../utils';
import { GridTableContext } from './grid-table-context';

export function GridProvider<D extends object = {}>({
  children,
  totalRows,
  pageSize,
  options: tableOptions,
  fetchData: fetchDataProps,
  hidePagination = false
}: {
  children: React.ReactNode;
  totalRows?: number;
  pageSize?: number;
  options: Partial<TableOptions<D>>;
  hidePagination?: boolean;
  fetchData?: ({ pageIndex, pageSize, sortBy, globalFilter }: FetchData) => Promise<void>;
}): JSX.Element {
  const paginationSize: number[] = React.useMemo(() => [pageSize ?? 100, 200, 300], [pageSize]);
  const initialPageSize: number = paginationSize[0];
  const [showLoader, setShowLoader] = React.useState<boolean>(tableOptions.data === undefined);
  const totalRowMemo = React.useMemo(
    () => totalRows ?? tableOptions.data?.length ?? 0,
    [totalRows, tableOptions.data]
  );

  // Memoize table options to prevent unnecessary re-renders
  const memoizedTableOptions = React.useMemo(
    () => ({
      columns: [],
      data: [],
      initialState: { pageIndex: 0, pageSize: initialPageSize },
      manualPagination: true,
      manualSortBy: true,
      disableMultiSort: true,
      defaultCanSort: false,
      autoResetPage: false,
      manualGlobalFilter: true,
      autoResetGlobalFilter: false,
      pageCount: Math.ceil(totalRowMemo / initialPageSize),
      ...tableOptions
    }),
    [initialPageSize, totalRowMemo, tableOptions]
  );

  const instance = useTable(memoizedTableOptions, useGlobalFilter, useSortBy, usePagination);

  // Only update columnsOrder when allColumns actually changes (deep comparison)
  const [columnsOrder, setColumnsOrder] = React.useState<ColumnInstance<D>[]>(instance.allColumns);

  React.useEffect(() => {
    // Use a more efficient way to check if columns have changed
    const columnsChanged =
      JSON.stringify(instance.allColumns.map((col) => col.id)) !==
      JSON.stringify(columnsOrder.map((col) => col.id));

    if (columnsChanged) {
      setColumnsOrder(instance.allColumns);
    }
  }, [instance.allColumns]);

  const mounted = useMounted();

  // Extract state values to use in dependencies
  const { pageIndex, pageSize: currentPageSize, sortBy, globalFilter } = instance.state;

  // Debounce global filter changes to prevent too many requests
  const debouncedGlobalFilter = React.useMemo(() => {
    return globalFilter ? globalFilter.trim().replace(/\s+/g, ' ') : globalFilter;
  }, [globalFilter]);

  // Memoize the fetch function to reduce unnecessary re-renders
  const fetchData = React.useCallback(async (): Promise<void> => {
    if (!fetchDataProps) {
      return;
    }

    // Only show loader for operations that take time
    setShowLoader(true);

    const sortColumn = getSortColumn(sortBy);
    const isUnmountedError = 'GridTable is unmounted';

    try {
      const response = await fetchDataProps({
        pageIndex,
        pageSize: currentPageSize,
        sortBy: sortColumn,
        globalFilter: debouncedGlobalFilter
      });

      if (mounted.current) {
        return response;
      }
      throw isUnmountedError;
    } catch (err) {
      if (err !== isUnmountedError) {
        throw err;
      }
    } finally {
      if (mounted.current) {
        setShowLoader(false);
      }
    }
  }, [fetchDataProps, pageIndex, currentPageSize, sortBy, debouncedGlobalFilter, mounted]);

  // Use a more controlled approach to when fetchData is called
  React.useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
    }, 100); // Small delay to batch rapid changes

    return (): void => clearTimeout(timer);
  }, [fetchData]);

  // Memoize the context value to prevent unnecessary re-renders of consumers
  const contextValue = React.useMemo(
    () => ({
      instance,
      paginationSize,
      showLoader,
      setShowLoader,
      totalRows: totalRowMemo,
      fetchData,
      hidePagination,
      columnsOrder,
      setColumnsOrder
    }),
    [instance, paginationSize, showLoader, totalRowMemo, fetchData, hidePagination, columnsOrder]
  );

  return <GridTableContext.Provider value={contextValue}>{children}</GridTableContext.Provider>;
}
